import { useMemo, useState } from "react";
import type { VillageViewModel } from "../../../domain/contracts";

type MapInteractionResult = {
  panTarget: [number, number] | null;
  setPanTarget: (target: [number, number] | null) => void;
  zoomTarget: number | null;
  setZoomTarget: (zoom: number | null) => void;
  activeVillage: VillageViewModel | null;
  center: [number, number];
  hasMappedPreviewVillages: boolean;
  isPinnedSelection: boolean;
  handleMarkerHover: (villageId: string) => void;
  handleMarkerLeave: (villageId: string) => void;
  handleMarkerClick: (village: VillageViewModel) => void;
  clearPinnedVillage: () => void;
};

export function useMapInteraction(
  villages: VillageViewModel[],
  selectedVillage: VillageViewModel | null,
): MapInteractionResult {
  const [hoveredVillageId, setHoveredVillageId] = useState<string | null>(null);
  const [pinnedReferenceVillageId, setPinnedReferenceVillageId] = useState<string | null>(null);
  const [panTarget, setPanTarget] = useState<[number, number] | null>(null);
  const [zoomTarget, setZoomTarget] = useState<number | null>(null);

  const hoveredVillage = useMemo(
    () => hoveredVillageId ? villages.find((item) => item.id === hoveredVillageId) ?? null : null,
    [hoveredVillageId, villages],
  );

  const pinnedReferenceVillage = useMemo(
    () => pinnedReferenceVillageId ? villages.find((item) => item.id === pinnedReferenceVillageId) ?? null : null,
    [pinnedReferenceVillageId, villages],
  );

  const activeVillage = selectedVillage ?? pinnedReferenceVillage ?? hoveredVillage ?? null;

  const center: [number, number] = activeVillage && (activeVillage.coordinates.lat !== 0 || activeVillage.coordinates.lng !== 0)
    ? [activeVillage.coordinates.lat, activeVillage.coordinates.lng]
    : [20.95, 92.42];

  const hasMappedPreviewVillages = villages.some(
    (v) => v.dataSource === "preview-backed" && v.stats.interviewCount > 0,
  );

  const isPinnedSelection = Boolean(selectedVillage || pinnedReferenceVillage);

  function handleMarkerHover(villageId: string): void {
    if (!selectedVillage && !pinnedReferenceVillageId) {
      setHoveredVillageId(villageId);
    }
  }

  function handleMarkerLeave(villageId: string): void {
    setHoveredVillageId((current) => current === villageId ? null : current);
  }

  function handleMarkerClick(village: VillageViewModel): void {
    setHoveredVillageId(null);
    setPinnedReferenceVillageId(village.id);
    setPanTarget([village.coordinates.lat, village.coordinates.lng]);
  }

  function clearPinnedVillage(): void {
    setPinnedReferenceVillageId(null);
    setHoveredVillageId(null);
    setZoomTarget(null);
  }

  return {
    panTarget,
    setPanTarget,
    zoomTarget,
    setZoomTarget,
    activeVillage,
    center,
    hasMappedPreviewVillages,
    isPinnedSelection,
    handleMarkerHover,
    handleMarkerLeave,
    handleMarkerClick,
    clearPinnedVillage,
  };
}